import { DETECTION_CONFIG } from "./constants.js";

/**
 * Gère la configuration utilisateur
 * Note: localStorage n'est pas disponible dans les artifacts Claude.ai
 * Cette version utilise uniquement la mémoire (pas de persistence)
 */
class ConfigManager {
  constructor() {
    this.STORAGE_KEY = "dupscan_config";
    // Valeurs par défaut (depuis constants.js)
  this.defaultConfig = {
    seuilMoyenneConnecteurs: DETECTION_CONFIG.seuilMoyenneConnecteurs,
    seuilMoyenneEpissures: DETECTION_CONFIG.seuilMoyenneEpissures,
    seuilPerteTotale: DETECTION_CONFIG.seuilPerteTotale,
  };

  }

  /**
   * Récupère la configuration active
   * @returns {Object} Configuration active
   */
  getConfig() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);

      if (stored) {
        const parsed = JSON.parse(stored);

        const validation = this.validateConfig(parsed);
        if (validation.isValid) {
          return parsed;
        }

        console.warn("Config stockée invalide, retour aux valeurs par défaut");
        this.resetConfig();
      }
    } catch (error) {
      console.error("Erreur lecture localStorage:", error);
    }

    return { ...this.defaultConfig };
  }

  /**
   * Sauvegarde la configuration (en mémoire uniquement)
   * @param {Object} config - Nouvelle configuration
   * @returns {Object} { success: boolean, errors?: string[] }
   */
  saveConfig(config) {
    const validation = this.validateConfig(config);

    if (!validation.isValid) {
      return {
        success: false,
        errors: validation.errors,
      };
    }

    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(config));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        errors: ["Erreur sauvegarde: " + error.message],
      };
    }
  }

  /**
   * Réinitialise la configuration
   */
  resetConfig() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error("Erreur suppression localStorage:", error);
    }
  }

  /**
   * Vérifie si la configuration actuelle est personnalisée
   * @returns {boolean} true si différente des valeurs par défaut
   */
  isCustomConfig() {
    return JSON.stringify(this.currentConfig) !== JSON.stringify(this.defaultConfig);
  }

  /**
   * Valide une configuration
   * @param {Object} config - Configuration à valider
   * @returns {Object} { isValid: boolean, errors: string[] }
   */
  validateConfig(config) {
    const errors = [];

    // seuilMoyenneConnecteurs : doit être > 0 et <= 1 (en dB)
    if (
      typeof config.seuilMoyenneConnecteurs !== "number" ||
      config.seuilMoyenneConnecteurs <= 0 ||
      config.seuilMoyenneConnecteurs > 1
    ) {
      errors.push("Seuil Moyenne connecteurs doit être entre 0 et 1 dB");
    }

    // seuilMoyenneEpissures : doit être > 0 et <= 1 (en dB)
    if (
      typeof config.seuilMoyenneEpissures !== "number" ||
      config.seuilMoyenneEpissures <= 0 ||
      config.seuilMoyenneEpissures > 1
    ) {
      errors.push("Seuil Moyenne épissures doit être entre 0 et 1 dB");
    }

    // seuilPerteTotale : doit être > 0 et <= 2 (en dB)
    if (typeof config.seuilPerteTotale !== "number" || config.seuilPerteTotale <= 0 || config.seuilPerteTotale > 2) {
      errors.push("Seuil Perte totale doit être entre 0 et 2 dB");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Récupère les valeurs par défaut
   * @returns {Object} Configuration par défaut
   */
  getDefaultConfig() {
    return { ...this.defaultConfig };
  }
}

// Instance singleton
export default new ConfigManager();
